import { Connection } from '@wix/cms-viewer-core'
import { ADDRESSINPUT_ROLE, BUTTON_ROLE, CHECKBOX_ROLE, IMAGE_ROLE, TEXT_ROLE } from '@wix/wix-data-client-common-standalone'
import { FieldType } from '@wix/wix-data-schema-types'
import { PlatformConnection } from './entities/platform-connection'

export const convertOldConnectionToNew = (connection, newNestedConnectionDescriptor = undefined, originalPath = undefined) => {
  const { compId, role, config: { properties = {}, expressions = {}, behaviors = [], totalCount } } = connection
  const newBehaviors = convertBehaviors(behaviors)
  const newConnectionDescriptor = newNestedConnectionDescriptor ?? Connection.makeDescriptor({ behaviors: newBehaviors })

  for (const [path, propertyConnectionDescriptor] of Object.entries({ ...properties, ...expressions })) {
    const pathParts = path.split('.')
    if (pathParts.length > 1) {
      const propName = pathParts[0]
      const remainingPath = pathParts.slice(1).join('.')
      const nestedConnectionDescriptor = PlatformConnection.make({
        role,
        config: { properties: { [remainingPath]: propertyConnectionDescriptor } },
      })
      newConnectionDescriptor.properties = {
        ...newConnectionDescriptor.properties,
        [propName]: convertOldConnectionToNew(nestedConnectionDescriptor, newConnectionDescriptor.properties[propName], originalPath ?? path),
      }
    } else if (path === '$text') {
      newConnectionDescriptor.dynamicProperties.text = convertToTextDynamicProperty(propertyConnectionDescriptor)
    } else if (path === 'link') {
      newConnectionDescriptor.extraData = convertToExtraData(propertyConnectionDescriptor)
      newConnectionDescriptor.dynamicProperties.link = convertToLinkDynamicProperty(propertyConnectionDescriptor)
    } else {
      newConnectionDescriptor.extraData = convertToExtraData(propertyConnectionDescriptor)
      newConnectionDescriptor.properties[path] = convertToNewProperty(role, originalPath ?? path, propertyConnectionDescriptor)
    }
  }

  if (totalCount) {
    newConnectionDescriptor.dynamicProperties.totalCount = convertToTotalCountDynamicProperty(totalCount)
  }

  return compId ? Connection.make({ compId, ...newConnectionDescriptor }) : newConnectionDescriptor
}

const convertToNewProperty = (role, path, { fieldName, expression, format }) => ({
  fieldName,
  expression,
  format,
  ...makeDataTypeDescriptor(role, path),
})

const makeDataTypeDescriptor = (role, path) => {
  const defaultFieldType = bindingDefinitions[role]?.[path]
  const convertFieldTypeToDataType = fieldTypeToDataTypeConverters[defaultFieldType] ?? fieldTypeToDataTypeConverters.default
  const dataTypeDescriptor = convertFieldTypeToDataType()
  return dataTypeDescriptor
}

const convertToTextDynamicProperty = ({ fieldName, format, expression, ...rest }) => ({
  fieldName, format, expression,
  propName: 'text',
  typeMapping: {
    richtext: 'html',
    url: '_hack_link', // special prop for component adapter being able to do hack
  },
  extraData: convertToExtraData(rest),
})

const convertToLinkDynamicProperty = ({ fieldName }) => ({
  fieldName,
  propName: 'link',
})

const convertToTotalCountDynamicProperty = ({ prop }) => ({ propName: prop === '$text' ? 'text' : prop })

const convertToExtraData = ({ linkRel, linkTarget }) => {
  const extraDataEntries = []
  if (linkRel !== undefined) {
    extraDataEntries.push(['rel', linkRel])
  }
  if (linkTarget !== undefined) {
    extraDataEntries.push(['target', linkTarget])
  }
  return extraDataEntries.length > 0 ? Object.fromEntries(extraDataEntries) : {}
}

// In old contract behavior could be only one - success or failure
const convertBehaviors = ([behavior]) => behavior ? {
  onSubmit: {
    type: oldBehaviorToTypeToNew[behavior.type],
    show: 'show',
    hide: 'hide',
  },
} : {}

const oldBehaviorToTypeToNew = {
  saveSuccessFeedback: 'success',
  saveFailureFeedback: 'failure',
}

const bindingDefinitions = {
  [TEXT_ROLE]: {
    $text: FieldType.text,
    'style.color': FieldType.color,
  },
  [IMAGE_ROLE]: {
    src: FieldType.image,
    alt: FieldType.text,
    tooltip: FieldType.text,
    link: FieldType.url,
  },
  [BUTTON_ROLE]: {
    label: FieldType.text,
    link: FieldType.url,
    'accessibility.ariaLabel': FieldType.text,
    'style.color': FieldType.color,
    'style.iconColor': FieldType.color,
    'style.borderColor': FieldType.color,
    'style.backgroundColor': FieldType.color,
    'style.hover.color': FieldType.color,
    'style.hover.iconColor': FieldType.color,
    'style.hover.borderColor': FieldType.color,
    'style.hover.backgroundColor': FieldType.color,
    'style.disabled.color': FieldType.color,
    'style.disabled.iconColor': FieldType.color,
    'style.disabled.borderColor': FieldType.color,
    'style.disabled.backgroundColor': FieldType.color,
  },
  [CHECKBOX_ROLE]: {
    checked: FieldType.boolean,
  },
  [ADDRESSINPUT_ROLE]: {
    value: FieldType.address,
  },
}

const fieldTypeToDataTypeConverters = {
  [FieldType.text]: () => ({ dataType: 'text' }),
  // despite there is dateType: 'image' in new component model,
  // old velo api expects wix:image://url as is, without any conversions
  [FieldType.image]: () => ({ dataType: 'text' }),
  [FieldType.boolean]: () => ({ dataType: 'boolean' }),
  [FieldType.address]: () => ({ dataType: 'schema', schema: {} }),
  [FieldType.color]: () => ({ dataType: 'unknown' }),
  [FieldType.url]: () => ({ dataType: 'text' }),
  default: () => ({ dataType: 'unknown' }),
}
